import { useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useGallery,
  usePropertyField,
  useSlotGroup,
  EditableSlotGroup,
  EditableToken,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHeroImage } from '../utils/heroImageUtils';
import { HeroSubtitleRichText } from './HeroSubtitleRichText';

/**
 * Price Brothers override of the Vantage PageHero.
 *
 * Why this override exists:
 *   The inherited component centers the title inside a short 30vh hero
 *   with no header-offset padding. On every common viewport the fixed,
 *   transparent site header overlays and visually eats the title.
 *
 * How this fix is bulletproof against future regressions:
 *   Every critical layout and visibility property is set via inline
 *   style props (not CSS classes), so cascade / specificity / caching /
 *   Tailwind preflight cannot swallow the title. The title + subtitle
 *   stack is also *bottom-anchored* (`justify-content: flex-end`) rather
 *   than vertically centered — a header overlay at the TOP of the hero
 *   can therefore never cover text at the BOTTOM, regardless of
 *   viewport height, offer banner height, or logo size.
 *
 * It also adds a HeroSolidBG-style editable subtitle rendered above the
 * title (matching the inner-page copy pattern used by Floor Plans,
 * Amenities, FAQ, etc.) so simple pages like Townhomes carry a short
 * tagline without needing to swap heroes to HeroSolidBG.
 */

interface PageHeroProps {
  tokenPrefix?: string;
  title?: string;
  /** Short tagline shown above the title. Rich text — may contain
      <span data-field="..."> property-field tokens. */
  subtitle?: string;
  /** Explicit default for subtitle (same shape as subtitle). */
  subtitleDefault?: string;
  altText?: string;
  keywords?: string[];
  pageHint?: string;
}

const SLOT_GROUP_DEF: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// Enough space to clear the fixed site header (~130px desktop) plus breathing
// room, so a block-flow (not centered) title can never slide behind it.
const HERO_HEIGHT = 'clamp(360px, 50vh, 560px)';

const FIELD = (name: string) =>
  `<span data-type="field-token" data-field="${name}"></span>`;

// Per-page subtitle defaults, keyed by tokenPrefix. Mirrors the pattern and
// copy style used by HeroSolidBG's SUBTITLE_DEFAULTS for the same unit-type
// prefixes, so pages that use PageHero carry a sensible tagline out of the
// box and read consistently with the rest of the site.
const SUBTITLE_DEFAULTS: Record<string, string> = {
  'studios-hero':
    `<p>Studio apartments at ${FIELD('property_name')} in ${FIELD('property_address_city')}, ${FIELD('property_address_state')}</p>`,
  '1-bedrooms-hero':
    `<p>1 bedroom apartments at ${FIELD('property_name')} in ${FIELD('property_address_city')}, ${FIELD('property_address_state')}</p>`,
  '2-bedroom-hero':
    `<p>2 bedroom apartments at ${FIELD('property_name')} in ${FIELD('property_address_city')}, ${FIELD('property_address_state')}</p>`,
  'townhomes-hero':
    `<p>Townhome rentals at ${FIELD('property_name')} in ${FIELD('property_address_city')}, ${FIELD('property_address_state')}</p>`,
};

const titleStyle: React.CSSProperties = {
  fontFamily: '"Raleway", sans-serif',
  fontWeight: 700,
  lineHeight: 1.05,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: '#ffffff',
  textShadow: '0 2px 20px rgba(0,0,0,0.45), 0 0 24px rgba(0,0,0,0.3)',
  margin: 0,
  padding: 0,
  // Fluid size — guaranteed large and visible on every viewport.
  fontSize: 'clamp(40px, 7vw, 76px)',
  maxWidth: '20ch',
  display: 'block',
  visibility: 'visible',
  opacity: 1,
};

const subtitleStyle: React.CSSProperties = {
  fontFamily: '"Raleway", sans-serif',
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.02em',
  textTransform: 'none',
  color: 'rgba(255,255,255,0.92)',
  textShadow: '0 1px 10px rgba(0,0,0,0.4)',
  margin: '0 0 0.875rem',
  padding: 0,
  // Fluid size — matches HeroSolidBG subtitle sizing.
  fontSize: 'clamp(17px, 1.6vw, 22px)',
  maxWidth: 'min(40ch, 92vw)',
  display: 'block',
  visibility: 'visible',
  opacity: 1,
};

export function PageHero({
  tokenPrefix,
  title = 'Page',
  subtitle,
  subtitleDefault,
  altText = 'Page banner',
  keywords = [],
  pageHint = '',
}: PageHeroProps) {
  const prefix = tokenPrefix || 'pagehero';
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const bgPath = `${prefix}.bg_image`;

  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');
  const titleValue = useContentToken(titlePath, { defaultValue: title });
  const bgImage = useSlotGroup(bgPath, SLOT_GROUP_DEF);

  const photoIndex = useMemo(
    () => pickHeroImage(gallery, keywords, pageHint),
    [gallery, keywords, pageHint],
  );

  const displayImage =
    bgImage.image ||
    (photoIndex >= 0 ? gallery[photoIndex]?.image : null) ||
    featuredImage ||
    gallery[0]?.image ||
    null;

  const resolvedSubtitleDefault =
    subtitleDefault ||
    subtitle ||
    SUBTITLE_DEFAULTS[prefix] ||
    `<p>Discover ${FIELD('property_name')}</p>`;

  // Pre-load Raleway (matches HeroSolidBG) so the font is ready when the
  // title paints.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="raleway"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'raleway');
    document.head.appendChild(link);
  }, []);

  return (
    <section
      className="pb-page-hero hero-rail-anchor"
      style={{
        position: 'relative',
        width: '100%',
        height: HERO_HEIGHT,
        minHeight: 360,
        overflow: 'hidden',
        // Hero rail CSS vars so anything inside that reads them (e.g. the
        // Header's floating hamburger) sizes correctly.
        ['--hero-rail-slot-count' as string]: 5,
        ['--hero-rail-width' as string]:
          'max(168px, min(280px, calc(100vh / 5)))',
        ['--hero-rail-hamburger-height' as string]: 'var(--hero-rail-width)',
      }}
    >
      {/* Background image — absolute, behind everything */}
      <EditableSlotGroup
        groupPath={bgPath}
        definition={SLOT_GROUP_DEF}
        className="absolute inset-0"
        style={{ position: 'absolute', inset: 0, zIndex: 0 }}
      >
        {displayImage ? (
          <VibesitesImage
            url={displayImage}
            alt={bgImage.alt || altText}
            priority
            sizes="100vw"
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'var(--vibe-color-text, #222)',
            }}
          />
        )}
      </EditableSlotGroup>

      {/* Dark gradient overlay */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          background:
            'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.15))',
          pointerEvents: 'none',
        }}
      />

      {/* Subtitle (above) + title (below) stack. Block-flow positioned near
          the bottom of the hero so the fixed header can never cover them,
          regardless of viewport height or offer-banner height. */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          textAlign: 'center',
          padding:
            '0 1.5rem calc(2rem + env(safe-area-inset-bottom)) 1.5rem',
          color: '#ffffff',
          boxSizing: 'border-box',
        }}
      >
        <HeroSubtitleRichText
          path={subtitlePath}
          defaultValue={resolvedSubtitleDefault}
          className="pb-page-hero-subtitle"
        />
        <EditableToken
          path={titlePath}
          as="h1"
          className="pb-page-hero-title"
          style={titleStyle}
        >
          {titleValue || title}
        </EditableToken>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <EditableToken
        path="pagehero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
      <EditableToken
        path="pagehero.subtitle"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      >
        {''}
      </EditableToken>
      <EditableSlotGroup
        groupPath="pagehero.bg_image"
        definition={SLOT_GROUP_DEF}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      {/* HeroSubtitleRichText applies className but no inline styles. Style
          the subtitle class here so its rich-text content renders at the
          intended size/weight/color. Scoped resets handle any <p>, <h1-3>,
          or <span> the editor stores inside the subtitle. */}
      <style>{`
        .pb-page-hero-subtitle {
          font-family: ${subtitleStyle.fontFamily};
          font-weight: ${subtitleStyle.fontWeight};
          line-height: ${subtitleStyle.lineHeight};
          letter-spacing: ${subtitleStyle.letterSpacing};
          text-transform: ${subtitleStyle.textTransform};
          color: ${subtitleStyle.color};
          text-shadow: ${subtitleStyle.textShadow};
          margin: ${subtitleStyle.margin};
          padding: 0;
          font-size: ${subtitleStyle.fontSize};
          max-width: ${subtitleStyle.maxWidth};
          display: ${subtitleStyle.display};
          visibility: ${subtitleStyle.visibility};
          opacity: 1;
        }
        .pb-page-hero-subtitle p,
        .pb-page-hero-subtitle h1,
        .pb-page-hero-subtitle h2,
        .pb-page-hero-subtitle h3,
        .pb-page-hero-subtitle span {
          font: inherit;
          color: inherit;
          letter-spacing: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageHero',
  displayName: 'Page Hero',
  tokens: {
    'pagehero.title': {
      label: 'Title',
      type: 'text',
      defaultValue: 'Page',
    },
    'pagehero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'pagehero.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page banner' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(PageHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageHero;
